import React from 'react'
import { Grid, GridProps } from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'

/** Viewport width &lt; 600px */
const XXS_MAX = 599
/** Viewport width &lt; 360px */
const XXXS_MAX = 359
/** Viewport width &lt; 260px */
const XXXXS_MAX = 259

type GridBreakpointSize = NonNullable<GridProps['xs']>

export type ExtraGridBreakpoints = {
  /** Column span for viewports narrower than 600px (`max-width: 599px`). */
  xxs?: GridBreakpointSize
  /** Column span for viewports narrower than 360px (`max-width: 359px`). */
  xxxs?: GridBreakpointSize
  /** Column span for viewports narrower than 260px (`max-width: 259px`). */
  xxxxs?: GridBreakpointSize
}

export type GridItemProps = Omit<GridProps, 'container'> & ExtraGridBreakpoints

type StyleProps = ExtraGridBreakpoints

/** Matches MUI `Grid` width math for numeric / `auto` / `true`. */
function gridSizeToFlex(
  size: GridBreakpointSize
): Record<string, string | number> {
  if (size === false) {
    return {}
  }
  if (size === true) {
    return {
      flexBasis: 0,
      flexGrow: 1,
      maxWidth: '100%',
    }
  }
  if (size === 'auto') {
    return {
      flexBasis: 'auto',
      flexGrow: 0,
      maxWidth: 'none',
    }
  }
  const width = `${Math.round((size / 12) * 10e7) / 10e5}%`
  return {
    flexBasis: width,
    flexGrow: 0,
    maxWidth: width,
  }
}

const useExtraBreakpointStyles = makeStyles<Theme, StyleProps>(() =>
  createStyles({
    root: ({ xxs, xxxs, xxxxs }) => {
      const media: Record<string, Record<string, string | number>> = {}
      if (xxs !== undefined) {
        media[`@media (max-width: ${XXS_MAX}px)`] = gridSizeToFlex(xxs)
      }
      if (xxxs !== undefined) {
        media[`@media (max-width: ${XXXS_MAX}px)`] = gridSizeToFlex(xxxs)
      }
      if (xxxxs !== undefined) {
        media[`@media (max-width: ${XXXXS_MAX}px)`] = gridSizeToFlex(xxxxs)
      }
      return Object.keys(media).length ? ({ '&&': media } as const) : {}
    },
  })
)

/** MUI `Grid` with `item` fixed; supports `xs` / `sm` / … plus `xxs` / `xxxs` / `xxxxs` for narrow viewports. */
export const GridItem: React.FC<GridItemProps> = ({
  children,
  className,
  xxs,
  xxxs,
  xxxxs,
  ...rest
}): React.ReactElement => {
  const classes = useExtraBreakpointStyles({ xxs, xxxs, xxxxs })
  const mergedClassName =
    [className, classes.root].filter(Boolean).join(' ') || undefined

  return (
    <Grid {...rest} item className={mergedClassName}>
      {children}
    </Grid>
  )
}
